import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, toConsumableArray as _toConsumableArray, defineProperty as _defineProperty, typeof as _typeof } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, useRef, useState } from 'react';
import { useId, useUncontrolled, useScrollIntoView, useDidUpdate, useClickOutside, useMergedRef } from '@mantine/hooks';
import { getDefaultZIndex } from '@mantine/styles';
import { groupOptions } from '@mantine/utils';
import { extractSystemStyles, Input } from '@mantine/core';
import { DefaultValue } from '../DefaultValue/DefaultValue.js';
import { DefaultItem } from '../DefaultItem/DefaultItem.js';
import { filterData } from '../filter-data/multi-filter-data.js';
import { getSelectRightSectionProps } from '../SelectRightSection/get-select-right-section-props.js';
import { SelectScrollArea } from '../SelectScrollArea/SelectScrollArea.js';
import { SelectPopover } from '../SelectPopover/SelectPopover.js';
import { SelectItems } from '../SelectItems/SelectItems.js';
import useStyles from './MultiSelect.styles.js';

var _excluded = ["className", "style", "required", "label", "description", "size", "error", "classNames", "styles", "wrapperProps", "value", "defaultValue", "data", "onChange", "valueComponent", "itemComponent", "id", "transition", "transitionDuration", "transitionTimingFunction", "maxDropdownHeight", "nothingFound", "onFocus", "onBlur", "searchable", "placeholder", "filter", "enhancedFilter", "limit", "clearSearchOnChange", "clearable", "clearSearchOnBlur", "clearButtonLabel", "variant", "onSearchChange", "searchValue", "disabled", "initiallyOpened", "radius", "icon", "rightSection", "rightSectionWidth", "creatable", "getCreateLabel", "shouldCreate", "onCreate", "sx", "dropdownComponent", "onDropdownClose", "onDropdownOpen", "maxSelectedValues", "withinPortal", "switchDirectionOnFlip", "zIndex", "selectOnBlur", "name", "dropdownPosition", "errorProps", "labelProps", "descriptionProps", "clearButtonTabIndex", "form", "positionDependencies", "onKeyDown", "unstyled", "inputContainer", "inputWrapperOrder", "readOnly", "withAsterisk", "hoverOnSearchChange", "topSection", "popoverWidth", "popoverOffset", "disableSelectedItemFiltering", "disabledOriginSearchInput", "closeButton", "withOverlay", "targetComponent"];
function defaultFilter(value, selected, item) {
  var _item$label;
  if (selected) {
    return false;
  }
  return !!((_item$label = item.label) !== null && _item$label !== void 0 && _item$label.toLowerCase().trim().includes(value.toLowerCase().trim()));
}
function defaultShouldCreate(query, data) {
  return !!query && !data.some(function (item) {
    return item.value.toLowerCase() === query.toLowerCase();
  });
}
function filterValue(value, data) {
  if (!Array.isArray(value)) {
    return [];
  }
  if (data.length === 0) {
    return [];
  }
  var flatData = data.map(function (item) {
    if (_typeof(item) === 'object') {
      return item.value;
    }
    return item;
  });
  return value.filter(function (val) {
    return flatData.includes(val);
  });
}
var MultiSelect = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
    style = props.style,
    required = props.required,
    label = props.label,
    description = props.description,
    _props$size = props.size,
    size = _props$size === void 0 ? 'md' : _props$size,
    error = props.error,
    classNames = props.classNames,
    styles = props.styles,
    wrapperProps = props.wrapperProps,
    value = props.value,
    defaultValue = props.defaultValue,
    data = props.data,
    onChange = props.onChange,
    _props$valueComponent = props.valueComponent,
    Value = _props$valueComponent === void 0 ? DefaultValue : _props$valueComponent,
    _props$itemComponent = props.itemComponent,
    itemComponent = _props$itemComponent === void 0 ? DefaultItem : _props$itemComponent,
    id = props.id,
    _props$transition = props.transition,
    transition = _props$transition === void 0 ? 'pop-top-left' : _props$transition,
    _props$transitionDura = props.transitionDuration,
    transitionDuration = _props$transitionDura === void 0 ? 0 : _props$transitionDura;
    props.transitionTimingFunction;
    var _props$maxDropdownHei = props.maxDropdownHeight,
    maxDropdownHeight = _props$maxDropdownHei === void 0 ? 220 : _props$maxDropdownHei,
    nothingFound = props.nothingFound,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    _props$searchable = props.searchable,
    searchable = _props$searchable === void 0 ? false : _props$searchable,
    placeholder = props.placeholder,
    _props$filter = props.filter,
    filter = _props$filter === void 0 ? defaultFilter : _props$filter,
    enhancedFilter = props.enhancedFilter,
    _props$limit = props.limit,
    limit = _props$limit === void 0 ? Infinity : _props$limit,
    _props$clearSearchOnC = props.clearSearchOnChange,
    clearSearchOnChange = _props$clearSearchOnC === void 0 ? true : _props$clearSearchOnC,
    _props$clearable = props.clearable,
    clearable = _props$clearable === void 0 ? false : _props$clearable,
    _props$clearSearchOnB = props.clearSearchOnBlur,
    clearSearchOnBlur = _props$clearSearchOnB === void 0 ? false : _props$clearSearchOnB,
    clearButtonLabel = props.clearButtonLabel,
    variant = props.variant,
    onSearchChange = props.onSearchChange,
    searchValue = props.searchValue,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    _props$initiallyOpene = props.initiallyOpened,
    initiallyOpened = _props$initiallyOpene === void 0 ? false : _props$initiallyOpene,
    radius = props.radius,
    icon = props.icon,
    rightSection = props.rightSection,
    rightSectionWidth = props.rightSectionWidth,
    _props$creatable = props.creatable,
    creatable = _props$creatable === void 0 ? false : _props$creatable,
    getCreateLabel = props.getCreateLabel,
    _props$shouldCreate = props.shouldCreate,
    shouldCreate = _props$shouldCreate === void 0 ? defaultShouldCreate : _props$shouldCreate,
    onCreate = props.onCreate,
    sx = props.sx,
    dropdownComponent = props.dropdownComponent,
    onDropdownClose = props.onDropdownClose,
    onDropdownOpen = props.onDropdownOpen,
    _props$maxSelectedVal = props.maxSelectedValues,
    maxSelectedValues = _props$maxSelectedVal === void 0 ? Infinity : _props$maxSelectedVal,
    withinPortal = props.withinPortal,
    _props$switchDirectio = props.switchDirectionOnFlip,
    switchDirectionOnFlip = _props$switchDirectio === void 0 ? false : _props$switchDirectio,
    _props$zIndex = props.zIndex,
    zIndex = _props$zIndex === void 0 ? getDefaultZIndex('popover') : _props$zIndex,
    _props$selectOnBlur = props.selectOnBlur,
    selectOnBlur = _props$selectOnBlur === void 0 ? false : _props$selectOnBlur,
    name = props.name,
    _props$dropdownPositi = props.dropdownPosition,
    dropdownPosition = _props$dropdownPositi === void 0 ? 'flip' : _props$dropdownPositi,
    errorProps = props.errorProps,
    labelProps = props.labelProps,
    descriptionProps = props.descriptionProps,
    _props$clearButtonTab = props.clearButtonTabIndex,
    clearButtonTabIndex = _props$clearButtonTab === void 0 ? 0 : _props$clearButtonTab,
    form = props.form,
    _props$positionDepend = props.positionDependencies,
    positionDependencies = _props$positionDepend === void 0 ? [] : _props$positionDepend,
    onKeyDown = props.onKeyDown,
    unstyled = props.unstyled,
    inputContainer = props.inputContainer,
    inputWrapperOrder = props.inputWrapperOrder,
    readOnly = props.readOnly,
    withAsterisk = props.withAsterisk,
    hoverOnSearchChange = props.hoverOnSearchChange,
    topSection = props.topSection,
    popoverWidth = props.popoverWidth,
    popoverOffset = props.popoverOffset,
    disableSelectedItemFiltering = props.disableSelectedItemFiltering,
    disabledOriginSearchInput = props.disabledOriginSearchInput,
    closeButton = props.closeButton,
    withOverlay = props.withOverlay,
    TargetComponent = props.targetComponent,
    others = _objectWithoutProperties(props, _excluded);
  var _useStyles = useStyles({
      size: size,
      invalid: !!error
    }, {
      classNames: classNames,
      styles: styles,
      unstyled: unstyled,
      name: 'MultiSelect'
    }),
    classes = _useStyles.classes,
    cx = _useStyles.cx,
    theme = _useStyles.theme;
  var _extractSystemStyles = extractSystemStyles(others),
    systemStyles = _extractSystemStyles.systemStyles,
    rest = _extractSystemStyles.rest;
  var inputRef = useRef(null);
  var itemsRefs = useRef({});
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    dropdownRef = _useState2[0],
    setDropdownRef = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    targetInputRef = _useState4[0],
    setTargetInputRef = _useState4[1];
  var uuid = useId(id);
  var _useState5 = useState(initiallyOpened),
    _useState6 = _slicedToArray(_useState5, 2),
    dropdownOpened = _useState6[0],
    setDropdownOpened = _useState6[1];
  var _useState7 = useState(-1),
    _useState8 = _slicedToArray(_useState7, 2),
    hovered = _useState8[0],
    setHovered = _useState8[1];
  var _useState9 = useState('column'),
    _useState10 = _slicedToArray(_useState9, 2),
    direction = _useState10[0],
    setDirection = _useState10[1];
  var _useUncontrolled = useUncontrolled({
      value: searchValue,
      defaultValue: '',
      finalValue: undefined,
      onChange: onSearchChange
    }),
    _useUncontrolled2 = _slicedToArray(_useUncontrolled, 2),
    _searchValue = _useUncontrolled2[0],
    handleSearchChange = _useUncontrolled2[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    IMEOpen = _useState12[0],
    setIMEOpen = _useState12[1];
  var _useScrollIntoView = useScrollIntoView({
      duration: 0,
      offset: 5,
      cancelable: false,
      isList: true
    }),
    scrollIntoView = _useScrollIntoView.scrollIntoView,
    targetRef = _useScrollIntoView.targetRef,
    scrollableRef = _useScrollIntoView.scrollableRef;
  var isCreatable = creatable && typeof getCreateLabel === 'function';
  var createLabel = null;
  var formattedData = data.map(function (item) {
    return typeof item === 'string' ? {
      label: item,
      value: item
    } : item;
  });
  var sortedData = groupOptions({
    data: formattedData
  });
  var _useUncontrolled3 = useUncontrolled({
      value: filterValue(value, data),
      defaultValue: filterValue(defaultValue, data),
      finalValue: [],
      onChange: onChange
    }),
    _useUncontrolled4 = _slicedToArray(_useUncontrolled3, 2),
    _value = _useUncontrolled4[0],
    setValue = _useUncontrolled4[1];
  var valuesOverflow = useRef(!!maxSelectedValues && maxSelectedValues < _value.length);
  var handleValueRemove = function handleValueRemove(_val) {
    if (!readOnly) {
      var newValue = _value.filter(function (val) {
        return val !== _val;
      });
      setValue(newValue);
      if (!!maxSelectedValues && newValue.length < maxSelectedValues) {
        valuesOverflow.current = false;
      }
    }
  };
  var handleInputChange = function handleInputChange(event) {
    handleSearchChange(event.currentTarget.value);
    !disabled && !valuesOverflow.current && searchable && setDropdownOpened(true);
  };
  var handleInputFocus = function handleInputFocus(event) {
    typeof onFocus === 'function' && onFocus(event);
    !disabled && !valuesOverflow.current && searchable && setDropdownOpened(true);
  };
  var handleHiddenCloseButton = function handleHiddenCloseButton() {
    setHovered(-1);
    setDropdownOpened(false);
  };
  var filteredData = filterData({
    data: sortedData,
    searchable: searchable,
    searchValue: _searchValue,
    limit: limit,
    filter: filter,
    value: _value,
    disableSelectedItemFiltering: disableSelectedItemFiltering,
    enhancedFilter: enhancedFilter
  });
  var getNextIndex = function getNextIndex(index, nextItem, compareFn) {
    var i = index;
    while (compareFn(i)) {
      i = nextItem(i);
      if (!filteredData[i].disabled) return i;
    }
    return index;
  };
  useDidUpdate(function () {
    if (hoverOnSearchChange && _searchValue) {
      setHovered(0);
    } else {
      setHovered(-1);
    }
  }, [_searchValue, hoverOnSearchChange]);
  useDidUpdate(function () {
    if (!disabled && _value.length > data.length) {
      setDropdownOpened(false);
    }
    if (!!maxSelectedValues && _value.length < maxSelectedValues) {
      valuesOverflow.current = false;
    }
    if (!!maxSelectedValues && _value.length >= maxSelectedValues) {
      valuesOverflow.current = true;
      setDropdownOpened(false);
    }
  }, [_value]);
  var handleItemSelect = function handleItemSelect(item) {
    if (!readOnly) {
      clearSearchOnChange && handleSearchChange('');
      if (_value.includes(item.value)) {
        handleValueRemove(item.value);
      } else {
        if (item.creatable && typeof onCreate === 'function') {
          var createdItem = onCreate(item.value);
          if (typeof createdItem !== 'undefined' && createdItem !== null) {
            if (typeof createdItem === 'string') {
              setValue([].concat(_toConsumableArray(_value), [createdItem]));
            } else {
              setValue([].concat(_toConsumableArray(_value), [createdItem.value]));
            }
          }
        } else {
          setValue([].concat(_toConsumableArray(_value), [item.value]));
        }
        if (_value.length === maxSelectedValues - 1) {
          valuesOverflow.current = true;
          setDropdownOpened(false);
        }
        if (hovered === filteredData.length - 1) {
          setHovered(filteredData.length - 2);
        }
        if (filteredData.length === 1) {
          setDropdownOpened(false);
        }
      }
    }
  };
  var handleInputBlur = function handleInputBlur(event) {
    typeof onBlur === 'function' && onBlur(event);
    if (selectOnBlur && filteredData[hovered] && dropdownOpened) {
      handleItemSelect(filteredData[hovered]);
    }
    clearSearchOnBlur && handleSearchChange('');
  };
  var handleInputKeydown = function handleInputKeydown(event) {
    if (IMEOpen) {
      return;
    }
    onKeyDown === null || onKeyDown === void 0 || onKeyDown(event);
    if (readOnly) {
      return;
    }
    if (event.key !== 'Backspace' && !!maxSelectedValues && valuesOverflow.current) {
      return;
    }
    var isColumn = direction === 'column';
    var handleNext = function handleNext() {
      setHovered(function (current) {
        var nextIndex = getNextIndex(current, function (index) {
          return index + 1;
        }, function (index) {
          return index < filteredData.length - 1;
        });
        if (dropdownOpened) {
          var _filteredData$nextInd;
          targetRef.current = itemsRefs.current[(_filteredData$nextInd = filteredData[nextIndex]) === null || _filteredData$nextInd === void 0 ? void 0 : _filteredData$nextInd.value];
          scrollIntoView({
            alignment: isColumn ? 'end' : 'start'
          });
        }
        return nextIndex;
      });
    };
    var handlePrevious = function handlePrevious() {
      setHovered(function (current) {
        var nextIndex = getNextIndex(current, function (index) {
          return index - 1;
        }, function (index) {
          return index > 0;
        });
        if (dropdownOpened) {
          var _filteredData$nextInd2;
          targetRef.current = itemsRefs.current[(_filteredData$nextInd2 = filteredData[nextIndex]) === null || _filteredData$nextInd2 === void 0 ? void 0 : _filteredData$nextInd2.value];
          scrollIntoView({
            alignment: isColumn ? 'start' : 'end'
          });
        }
        return nextIndex;
      });
    };
    switch (event.key) {
      case 'ArrowUp':
        {
          event.preventDefault();
          setDropdownOpened(true);
          isColumn ? handlePrevious() : handleNext();
          break;
        }
      case 'ArrowDown':
        {
          event.preventDefault();
          setDropdownOpened(true);
          isColumn ? handleNext() : handlePrevious();
          break;
        }
      case 'Enter':
        {
          event.preventDefault();
          if (filteredData[hovered] && dropdownOpened) {
            handleItemSelect(filteredData[hovered]);
          } else {
            setDropdownOpened(true);
          }
          break;
        }
      case ' ':
        {
          if (!searchable) {
            event.preventDefault();
            if (filteredData[hovered] && dropdownOpened) {
              handleItemSelect(filteredData[hovered]);
            } else {
              setDropdownOpened(true);
            }
          }
          break;
        }
      case 'Backspace':
        {
          if (_value.length > 0 && _searchValue.length === 0) {
            setValue(_value.slice(0, -1));
            setDropdownOpened(true);
            if (maxSelectedValues) {
              valuesOverflow.current = false;
            }
          }
          break;
        }
      case 'Home':
        {
          if (!searchable) {
            event.preventDefault();
            if (!dropdownOpened) {
              setDropdownOpened(true);
            }
            var firstItemIndex = filteredData.findIndex(function (item) {
              return !item.disabled;
            });
            setHovered(firstItemIndex);
            scrollIntoView({
              alignment: isColumn ? 'end' : 'start'
            });
          }
          break;
        }
      case 'End':
        {
          if (!searchable) {
            event.preventDefault();
            if (!dropdownOpened) {
              setDropdownOpened(true);
            }
            var lastItemIndex = filteredData.map(function (item) {
              return !!item.disabled;
            }).lastIndexOf(false);
            setHovered(lastItemIndex);
            scrollIntoView({
              alignment: isColumn ? 'end' : 'start'
            });
          }
          break;
        }
      case 'Escape':
        {
          setDropdownOpened(false);
        }
    }
  };
  var selectedItems = _value.map(function (val) {
    var selectedItem = sortedData.find(function (item) {
      return item.value === val && !item.disabled;
    });
    if (!selectedItem && isCreatable) {
      selectedItem = {
        value: val,
        label: val
      };
    }
    return selectedItem;
  }).filter(function (val) {
    return !!val;
  }).map(function (item) {
    return /*#__PURE__*/React__default.createElement(Value, _objectSpread2(_objectSpread2({}, item), {}, {
      variant: variant,
      disabled: disabled,
      className: classes.value,
      readOnly: readOnly,
      onRemove: function onRemove(event) {
        event.preventDefault();
        event.stopPropagation();
        handleValueRemove(item.value);
      },
      key: item.value,
      size: size,
      styles: styles,
      classNames: classNames,
      radius: radius
    }));
  });
  var isItemSelected = function isItemSelected(itemValue) {
    return _value.includes(itemValue);
  };
  var handleClear = function handleClear() {
    var _inputRef$current;
    handleSearchChange('');
    setValue([]);
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
    if (maxSelectedValues) {
      valuesOverflow.current = false;
    }
  };
  useClickOutside(function () {
    setDropdownOpened(false);
  }, null, [dropdownRef, targetInputRef]);
  if (isCreatable && shouldCreate(_searchValue, sortedData)) {
    createLabel = getCreateLabel(_searchValue);
    filteredData.push({
      label: _searchValue,
      value: _searchValue,
      creatable: true
    });
  }
  var shouldRenderDropdown = !readOnly && (filteredData.length > 0 ? dropdownOpened : dropdownOpened && !!nothingFound);
  useDidUpdate(function () {
    var handler = shouldRenderDropdown ? onDropdownOpen : onDropdownClose;
    typeof handler === 'function' && handler();
  }, [shouldRenderDropdown]);
  return /*#__PURE__*/React__default.createElement(Input.Wrapper, _objectSpread2(_objectSpread2({
    required: required,
    id: uuid,
    label: label,
    error: error,
    description: description,
    size: size,
    className: className,
    style: style,
    classNames: classNames,
    styles: styles,
    __staticSelector: "MultiSelect",
    sx: sx,
    errorProps: errorProps,
    descriptionProps: descriptionProps,
    labelProps: labelProps,
    inputContainer: inputContainer,
    inputWrapperOrder: inputWrapperOrder,
    unstyled: unstyled,
    withAsterisk: withAsterisk
  }, systemStyles), wrapperProps), /*#__PURE__*/React__default.createElement(SelectPopover, {
    opened: shouldRenderDropdown,
    transition: transition,
    transitionDuration: transitionDuration,
    shadow: "sm",
    withinPortal: withinPortal,
    __staticSelector: "MultiSelect",
    onDirectionChange: setDirection,
    switchDirectionOnFlip: switchDirectionOnFlip,
    zIndex: zIndex,
    dropdownPosition: dropdownPosition,
    positionDependencies: [].concat(_toConsumableArray(positionDependencies), [_searchValue]),
    classNames: classNames,
    styles: styles,
    unstyled: unstyled,
    popoverWidth: popoverWidth,
    popoverOffset: popoverOffset
  }, /*#__PURE__*/React__default.createElement(SelectPopover.Target, null, TargetComponent ? ( /*#__PURE__*/React__default.createElement("div", {
    className: classes.wrapper,
    role: "combobox",
    "aria-haspopup": "listbox",
    "aria-owns": dropdownOpened && shouldRenderDropdown ? "".concat(uuid, "-items") : undefined,
    "aria-controls": uuid,
    "aria-expanded": dropdownOpened,
    onMouseLeave: function onMouseLeave() {
      return setHovered(-1);
    },
    tabIndex: -1,
    ref: setTargetInputRef
  }, /*#__PURE__*/React__default.createElement("input", {
    readOnly: true,
    name: name,
    value: _value.join(','),
    form: form,
    disabled: disabled,
    style: {
      opacity: 0,
      position: 'absolute',
      inset: 0,
      margin: 0,
      padding: 0,
      border: 'none',
      minWidth: 'unset',
      width: 'unset',
      cursor: disabled ? 'auto' : 'pointer'
    },
    onKeyDown: handleInputKeydown,
    onMouseDown: function onMouseDown(event) {
      var _inputRef$current2;
      event.preventDefault();
      !disabled && !valuesOverflow.current && setDropdownOpened(!dropdownOpened);
      (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.focus();
    },
    onBlur: handleInputBlur,
    onFocus: handleInputFocus
  }), /*#__PURE__*/React__default.createElement(TargetComponent, {
    ref: setTargetInputRef,
    "data-mantine-stop-propagation": dropdownOpened,
    disabled: disabled,
    tabIndex: -1,
    value: _value
  }))) : ( /*#__PURE__*/React__default.createElement("div", {
    className: classes.wrapper,
    role: "combobox",
    "aria-haspopup": "listbox",
    "aria-owns": dropdownOpened && shouldRenderDropdown ? "".concat(uuid, "-items") : undefined,
    "aria-controls": uuid,
    "aria-expanded": dropdownOpened,
    onMouseLeave: function onMouseLeave() {
      return setHovered(-1);
    },
    tabIndex: -1,
    ref: setTargetInputRef
  }, /*#__PURE__*/React__default.createElement("input", {
    type: "hidden",
    name: name,
    value: _value.join(','),
    form: form,
    disabled: disabled
  }), /*#__PURE__*/React__default.createElement(Input, _objectSpread2({
    __staticSelector: "MultiSelect",
    style: {
      overflow: 'hidden'
    },
    component: "div",
    "data-mantine-stop-propagation": dropdownOpened,
    multiline: true,
    size: size,
    variant: variant,
    disabled: disabled,
    invalid: !!error,
    required: required,
    radius: radius,
    icon: icon,
    unstyled: unstyled,
    onMouseDown: function onMouseDown(event) {
      var _inputRef$current3;
      event.preventDefault();
      !disabled && !valuesOverflow.current && setDropdownOpened(!dropdownOpened);
      (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 || _inputRef$current3.focus();
    },
    classNames: _objectSpread2(_objectSpread2({}, classNames), {}, {
      input: cx(_defineProperty({}, classes.input, !searchable), classNames === null || classNames === void 0 ? void 0 : classNames.input)
    })
  }, getSelectRightSectionProps({
    theme: theme,
    rightSection: rightSection,
    rightSectionWidth: rightSectionWidth,
    styles: styles,
    size: size,
    shouldClear: clearable && _value.length > 0,
    clearButtonLabel: clearButtonLabel,
    onClear: handleClear,
    error: error,
    disabled: disabled,
    clearButtonTabIndex: clearButtonTabIndex,
    readOnly: readOnly
  })), /*#__PURE__*/React__default.createElement("div", {
    className: classes.values
  }, selectedItems, /*#__PURE__*/React__default.createElement("input", _objectSpread2(_objectSpread2({
    id: uuid,
    className: cx(classes.searchInput, _defineProperty(_defineProperty(_defineProperty({}, classes.searchInputPointer, !searchable), classes.searchInputInputHidden, !dropdownOpened && _value.length > 0 || !searchable && _value.length > 0), classes.searchInputEmpty, _value.length === 0)),
    "aria-invalid": !!error,
    onKeyDown: handleInputKeydown,
    value: _searchValue,
    onChange: handleInputChange,
    onFocus: handleInputFocus,
    onBlur: handleInputBlur,
    readOnly: !searchable || valuesOverflow.current || readOnly || disabledOriginSearchInput,
    placeholder: _value.length === 0 ? placeholder : undefined,
    disabled: disabled,
    "data-mantine-stop-propagation": dropdownOpened
  }, rest), {}, {
    ref: useMergedRef(ref, inputRef),
    type: "search",
    autoComplete: "off",
    onCompositionStart: function onCompositionStart() {
      return setIMEOpen(true);
    },
    onCompositionEnd: function onCompositionEnd() {
      return setIMEOpen(false);
    }
  }))))))), /*#__PURE__*/React__default.createElement(SelectPopover.Dropdown, {
    component: dropdownComponent || SelectScrollArea,
    maxHeight: maxDropdownHeight,
    direction: direction,
    id: uuid,
    innerRef: scrollableRef,
    __staticSelector: "MultiSelect",
    classNames: classNames,
    styles: styles,
    topSection: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, closeButton ? closeButton(handleHiddenCloseButton) : null, topSection),
    dropdownRef: setDropdownRef,
    withOverlay: withOverlay,
    handleClickOverlay: function handleClickOverlay() {
      return setDropdownOpened(false);
    }
  }, /*#__PURE__*/React__default.createElement(SelectItems, {
    data: filteredData,
    hovered: hovered,
    classNames: classNames,
    styles: styles,
    uuid: uuid,
    __staticSelector: "MultiSelect",
    onItemHover: setHovered,
    onItemSelect: handleItemSelect,
    itemsRefs: itemsRefs,
    itemComponent: itemComponent,
    size: size,
    nothingFound: nothingFound,
    isItemSelected: isItemSelected,
    creatable: creatable && !!createLabel,
    createLabel: createLabel,
    unstyled: unstyled
  }))));
});

export { MultiSelect, defaultFilter, defaultShouldCreate };
//# sourceMappingURL=MultiSelect.js.map
